import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect, createContext, useContext } from 'react';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ClubsPage from './pages/ClubsPage';
import DashboardPage from './pages/DashboardPage';
import PlayersPage from './pages/PlayersPage';
import PlayerProfilePage from './pages/PlayerProfilePage';
import TournamentsPage from './pages/TournamentsPage';
import TournamentDetailPage from './pages/TournamentDetailPage';
import StandingsPage from './pages/StandingsPage';
import AdminPage from './pages/AdminPage';
import SettingsPage from './pages/SettingsPage';
import MobilePreviewPage from './pages/MobilePreviewPage';
import JoinClubPage from './pages/JoinClubPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import HelpPage from './pages/HelpPage';
import QuestionnairePage from './pages/QuestionnairePage';
import InvitesPage from './pages/InvitesPage';
import './App.css';

// ── Club Context ──────────────────────────────────────────────────────────────
const ClubContext = createContext(null);

export const useClub = () => {
  const ctx = useContext(ClubContext);
  if (!ctx) throw new Error('useClub must be used within ClubProvider');
  return ctx;
};

function ClubProvider({ children }) {
  const [selectedClub, setSelectedClub] = useState(() => {
    const saved = localStorage.getItem('selectedClub');
    if (saved) return JSON.parse(saved);
    const old = localStorage.getItem('selectedGroup');
    if (old) { localStorage.setItem('selectedClub', old); localStorage.removeItem('selectedGroup'); return JSON.parse(old); }
    return null;
  });
  const selectClub = (club) => { setSelectedClub(club); localStorage.setItem('selectedClub', JSON.stringify(club)); };
  const clearClub = () => { setSelectedClub(null); localStorage.removeItem('selectedClub'); };
  return <ClubContext.Provider value={{ selectedClub, selectClub, clearClub }}>{children}</ClubContext.Provider>;
}

// ── Theme Context ─────────────────────────────────────────────────────────────
const ThemeContext = createContext({ theme: 'light', setTheme: () => {} });
export const useTheme = () => useContext(ThemeContext);

function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');
    localStorage.setItem('theme', theme);
  }, [theme]);

  return <ThemeContext.Provider value={{ theme, setTheme }}>{children}</ThemeContext.Provider>;
}

// ── Route Guards ──────────────────────────────────────────────────────────────
function ProtectedRoute({ children, roles, skipQuestionnaire }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  if (!skipQuestionnaire && user.player_id && user.questionnaire_completed === false)
    return <Navigate to="/questionnaire" replace />;
  return children;
}

function ClubRequiredRoute({ children }) {
  const { user } = useAuth();
  const { selectedClub } = useClub();
  if (!user) return <Navigate to="/login" replace />;
  if (user.player_id && user.questionnaire_completed === false) return <Navigate to="/questionnaire" replace />;
  if (!selectedClub) return <Navigate to="/clubs" replace />;
  return children;
}

// ── App ───────────────────────────────────────────────────────────────────────
function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <ClubProvider>
            <BrowserRouter>
              <Toaster position="top-right" richColors />
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/questionnaire" element={<ProtectedRoute skipQuestionnaire><QuestionnairePage /></ProtectedRoute>} />
                <Route path="/clubs" element={<ProtectedRoute><ClubsPageWrapper /></ProtectedRoute>} />
                <Route path="/groups" element={<Navigate to="/clubs" replace />} />
                <Route path="/join/:code" element={<ProtectedRoute><JoinClubPage /></ProtectedRoute>} />
                <Route path="/" element={<ClubRequiredRoute><Layout /></ClubRequiredRoute>}>
                  <Route index element={<Navigate to="/dashboard" replace />} />
                  <Route path="dashboard" element={<DashboardPage />} />
                  <Route path="players" element={<PlayersPage />} />
                  <Route path="players/:id" element={<PlayerProfilePage />} />
                  <Route path="tournaments" element={<TournamentsPage />} />
                  <Route path="tournaments/:id" element={<TournamentDetailPage />} />
                  <Route path="standings" element={<StandingsPage />} />
                  <Route path="settings" element={<SettingsPage />} />
                  <Route path="help" element={<HelpPage />} />
                  <Route path="invites" element={<InvitesPage />} />
                  <Route path="admin" element={<AdminPage />} />
                </Route>
                <Route path="/mobile-preview" element={<MobilePreviewPage />} />
                <Route path="*" element={<Navigate to="/clubs" replace />} />
              </Routes>
            </BrowserRouter>
          </ClubProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

function ClubsPageWrapper() {
  const { selectClub } = useClub();
  return <ClubsPage onSelectClub={(club) => { selectClub(club); window.location.href = '/dashboard'; }} />;
}

export default App;
